import { useMemo, useState } from 'react';
import { fromJS } from 'immutable';
import {
	Chain,
	CheckboxCheckedGold,
	CheckboxUncheckedSmall,
	InfoWhite,
	NonChain,
	RadioButtonEmptyWhite,
	RadioButtonSelectedGold,
	StaffGreen,
	StaffOrange,
	SalesVolumeMoney,
	SquareFeetIcon
} from '@/resources/assets/images';
import { shallowEqual, useSelector } from 'react-redux';
import type { RootState } from '@/rootReducer';
import {
	BusinessQuery,
	Filter,
	FilterQueryType,
	FilterType
} from '@/modules/dashboard/dashboardTypes';
import { sendAnalytics } from '@/services/analyticsService';
import omit from 'lodash/omit';
import Button from '@/components/Button/newButtons';
import { isQueryDifferent } from '../src/helpers/isQueryDifferent';
import SearchTabFiltersListItemHeader from '../SearchTabFiltersListItemHeader';
import { useSearchTabContext } from '../SearchTabContext';
import './SearchTabBusinessInfo.scss';

type SearchTabBusinessInfoProps = {
	onFilterChange: (query: BusinessQuery) => void;
	onGetBusiness: (query: BusinessQuery) => void;
};

const SearchTabBusinessInfo = ({
	onFilterChange,
	onGetBusiness
}: SearchTabBusinessInfoProps) => {
	const [query, user] = useSelector(
		(state: RootState) => [
			state.dashboard.get('query').toJS(),
			state.user.getIn(['authorize', 'user']).toJS()
		],
		shallowEqual
	);
	const [businessInfoQuery, setBusinessInfoQuery] = useState<
		BusinessQuery | {}
	>({});

	const {
		selectedListItem,
		yelpReviewsFilterMax,
		yelpReviewsFilterMin,
		setYelpReviewsFilterMin,
		setYelpReviewsFilterMax
	} = useSearchTabContext();

	const yelpReviewsApplyButtonDisabledCheck =
		(yelpReviewsFilterMin === '' && yelpReviewsFilterMax === '') ||
		(typeof yelpReviewsFilterMin === 'number' &&
			typeof yelpReviewsFilterMax === 'number' &&
			yelpReviewsFilterMin > 0 &&
			yelpReviewsFilterMax < yelpReviewsFilterMin);

	const yelpReviewsFilterChange = (min: number, max?: number) => {
		const queryNew = fromJS(query).toJS();

		queryNew.minYelpReviews = min;
		queryNew.maxYelpReviews = max;

		queryNew.limit = 50;

		queryNew.skip = 0;

		queryNew.queryType = FilterQueryType.FILTER;

		onFilterChange(queryNew);
		onGetBusiness(queryNew);

		sendAnalytics({
			type: 'track',
			eventName: 'User changed filters',
			properties: {
				filterChanged: 'Yelp Reviews',
				query: queryNew
			}
		});
	};

	const yelpReviewsApplyButtonClickHandler = () => {
		if (yelpReviewsApplyButtonDisabledCheck) {
			return;
		}

		let minValue = yelpReviewsFilterMin as number | string | undefined;
		let maxValue = yelpReviewsFilterMax as number | string | undefined;

		if (minValue === '') {
			minValue = 0;
		}

		if (maxValue === '') {
			maxValue = undefined;
		}
		yelpReviewsFilterChange(minValue as number, maxValue as number);
	};

	const yelpReviewsClearButtonClickHandler = () => {
		setYelpReviewsFilterMax('');
		setYelpReviewsFilterMin('');
	};

	const clearFilters = () => {
		const queryNew = { ...query };

		delete queryNew.chains;
		delete queryNew.nonChains;

		delete queryNew.countOne;
		delete queryNew.countTwoTen;
		delete queryNew.countTenPlus;

		delete queryNew.includePOIs;
		delete queryNew.excludePOIs;

		delete queryNew.employees1To49;
		delete queryNew.employees50To99;
		delete queryNew.employees100To249;
		delete queryNew.employees250To499;
		delete queryNew.employeesOver500;

		delete queryNew.salesVolumeLessThan5M;
		delete queryNew.salesVolume5To10M;
		delete queryNew.salesVolume10To20M;
		delete queryNew.salesVolume20To50M;
		delete queryNew.salesVolume50To100M;
		delete queryNew.salesVolumeOver100M;

		delete queryNew.minYelpReviews;
		delete queryNew.maxYelpReviews;

		delete queryNew.sqftSmall;
		delete queryNew.sqftMedium;
		delete queryNew.sqftLarge;
		yelpReviewsClearButtonClickHandler();

		sendAnalytics({
			type: 'track',
			userId: user.email,
			properties: {
				query: queryNew
			},
			eventName: 'User cleared business info filters'
		});

		queryNew.skip = 0;

		const queryChanged = isQueryDifferent(queryNew);

		onFilterChange(queryNew);

		if (queryChanged) {
			onGetBusiness(queryNew);
		}
	};

	const queryChange = (filter: Filter) => {
		const queryNew = { ...query };

		if (filter.value === 'chains') {
			if (filter.type === FilterType.INCLUDES) {
				setBusinessInfoQuery(
					omit({ ...businessInfoQuery, chains: true }, 'nonChains')
				);
				queryNew.chains = true;
				delete queryNew.nonChains;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Chains',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				delete queryNew.chains;
				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Chains',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		}

		if (filter.value === 'nonChains') {
			if (filter.type === FilterType.INCLUDES) {
				setBusinessInfoQuery(
					omit({ ...businessInfoQuery, nonChains: true }, 'chains')
				);
				queryNew.nonChains = true;
				delete queryNew.chains;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Non-Chain',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				delete queryNew.nonChains;
				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Non-Chain',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		}

		if (filter.value === 'countOne') {
			if (filter.type === FilterType.INCLUDES) {
				setBusinessInfoQuery({ ...businessInfoQuery, countOne: true });
				queryNew.countOne = true;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Locations: 1',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				setBusinessInfoQuery(omit(businessInfoQuery, 'countOne'));
				delete queryNew.countOne;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Locations: 1',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		}

		if (filter.value === 'countTwoTen') {
			if (filter.type === FilterType.INCLUDES) {
				setBusinessInfoQuery({
					...businessInfoQuery,
					countTwoTen: true
				});
				queryNew.countTwoTen = true;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Locations: 2-10',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				setBusinessInfoQuery(omit(businessInfoQuery, 'countTwoTen'));
				delete queryNew.countTwoTen;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Locations: 2-10',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		}

		if (filter.value === 'countTenPlus') {
			if (filter.type === FilterType.INCLUDES) {
				setBusinessInfoQuery({
					...businessInfoQuery,
					countTenPlus: true
				});
				queryNew.countTenPlus = true;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Locations: 10+',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				setBusinessInfoQuery(omit(businessInfoQuery, 'countTenPlus'));
				delete queryNew.countTenPlus;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Locations: 10+',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		}

		if (filter.value === 'includePOIs') {
			if (filter.type === FilterType.INCLUDES) {
				setBusinessInfoQuery({
					...businessInfoQuery,
					includePOIs: true,
					excludePOIs: false
				});
				queryNew.includePOIs = true;
				delete queryNew?.excludePOIs;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Has staff info',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				setBusinessInfoQuery({
					...businessInfoQuery,
					includePOIs: false,
					excludePOIs: true
				});

				delete queryNew?.includePOIs;
				queryNew.excludePOIs = true;
				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Has staff info',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		}

		// Handling Sales Volume filter as strings
		if (filter.value === 'salesVolumeLessThan5M') {
			if (filter.type === FilterType.INCLUDES) {
				setBusinessInfoQuery({
					...businessInfoQuery,
					salesVolumeLessThan5M: true
				});
				queryNew.salesVolumeLessThan5M = true;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Sales Volume: Less than $5M',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				setBusinessInfoQuery(
					omit(businessInfoQuery, 'salesVolumeLessThan5M')
				);

				delete queryNew.salesVolumeLessThan5M;

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Sales Volume: Less than $5M',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		} else if (filter.value === 'salesVolume5To10M') {
			if (filter.type === FilterType.INCLUDES) {
				queryNew.salesVolume5To10M = true;
				setBusinessInfoQuery({
					...businessInfoQuery,
					salesVolume5To10M: true
				});
				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Sales Volume: $5 to $10M',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				delete queryNew.salesVolume5To10M;
				setBusinessInfoQuery({
					...businessInfoQuery,
					salesVolume5To10M: false
				});

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Sales Volume: $5 to $10M',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		} else if (filter.value === 'salesVolume10To20M') {
			if (filter.type === FilterType.INCLUDES) {
				queryNew.salesVolume10To20M = true;
				setBusinessInfoQuery({
					...businessInfoQuery,
					salesVolume10To20M: true
				});

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Sales Volume: $10 to $20M',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				delete queryNew.salesVolume10To20M;
				setBusinessInfoQuery({
					...businessInfoQuery,
					salesVolume10To20M: false
				});

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Sales Volume: $10 to $20M',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		} else if (filter.value === 'salesVolume20To50M') {
			if (filter.type === FilterType.INCLUDES) {
				queryNew.salesVolume20To50M = true;
				setBusinessInfoQuery({
					...businessInfoQuery,
					salesVolume20To50M: true
				});

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Sales Volume: $20 to $50M',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				delete queryNew.salesVolume20To50M;
				setBusinessInfoQuery({
					...businessInfoQuery,
					salesVolume20To50M: false
				});

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Sales Volume: $20 to $50M',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		} else if (filter.value === 'salesVolume50To100M') {
			if (filter.type === FilterType.INCLUDES) {
				queryNew.salesVolume50To100M = true;
				setBusinessInfoQuery({
					...businessInfoQuery,
					salesVolume50To100M: true
				});

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Sales Volume: $50 to $100M',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				delete queryNew.salesVolume50To100M;
				setBusinessInfoQuery({
					...businessInfoQuery,
					salesVolume50To100M: false
				});

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Sales Volume: $50 to $100M',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		} else if (filter.value === 'salesVolumeOver100M') {
			if (filter.type === FilterType.INCLUDES) {
				queryNew.salesVolumeOver100M = true;
				setBusinessInfoQuery({
					...businessInfoQuery,
					salesVolumeOver100M: true
				});
				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Sales Volume: Over $100M',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				delete queryNew.salesVolumeOver100M;
				setBusinessInfoQuery({
					...businessInfoQuery,
					salesVolumeOver100M: false
				});

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Sales Volume: Over $100M',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		}

		// Handling Number of Employees filter as strings
		if (filter.value === 'employees1To49') {
			if (filter.type === FilterType.INCLUDES) {
				queryNew.employees1To49 = true;
				setBusinessInfoQuery({
					...businessInfoQuery,
					employees1To49: true
				});

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Employees: 1 to 49',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				delete queryNew.employees1To49;
				setBusinessInfoQuery({
					...businessInfoQuery,
					employees1To49: false
				});

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Employees: 1 to 49',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		} else if (filter.value === 'employees50To99') {
			if (filter.type === FilterType.INCLUDES) {
				queryNew.employees50To99 = true;
				setBusinessInfoQuery({
					...businessInfoQuery,
					employees50To99: true
				});

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Employees: 50 to 99',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				delete queryNew.employees50To99;
				setBusinessInfoQuery({
					...businessInfoQuery,
					employees50To99: false
				});

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Employees: 50 to 99',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		} else if (filter.value === 'employees100To249') {
			if (filter.type === FilterType.INCLUDES) {
				queryNew.employees100To249 = true;
				setBusinessInfoQuery({
					...businessInfoQuery,
					employees100To249: true
				});

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Employees: 100 to 249',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				delete queryNew.employees100To249;
				setBusinessInfoQuery({
					...businessInfoQuery,
					employees100To249: false
				});

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Employees: 100 to 249',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		} else if (filter.value === 'employees250To499') {
			if (filter.type === FilterType.INCLUDES) {
				queryNew.employees250To499 = true;
				setBusinessInfoQuery({
					...businessInfoQuery,
					employees250To499: true
				});

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Employees: 250 to 499',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				delete queryNew.employees250To499;
				setBusinessInfoQuery({
					...businessInfoQuery,
					employees250To499: false
				});

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Employees: 250 to 499',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		} else if (filter.value === 'employeesOver500') {
			if (filter.type === FilterType.INCLUDES) {
				queryNew.employeesOver500 = true;
				setBusinessInfoQuery({
					...businessInfoQuery,
					employeesOver500: true
				});

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Employees: Over 500',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				delete queryNew.employeesOver500;
				setBusinessInfoQuery({
					...businessInfoQuery,
					employeesOver500: false
				});

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Employees: Over 500',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		}

		// Handling Sales Volume filter as strings
		if (filter.value === 'sqftSmall') {
			if (filter.type === FilterType.INCLUDES) {
				queryNew.sqftSmall = true;
				setBusinessInfoQuery({
					...businessInfoQuery,
					sqftSmall: true
				});

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Square Feet: Small',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				delete queryNew.sqftSmall;
				setBusinessInfoQuery({
					...businessInfoQuery,
					sqftSmall: false
				});

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Square Feet: Small',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		} else if (filter.value === 'sqftMedium') {
			if (filter.type === FilterType.INCLUDES) {
				queryNew.sqftMedium = true;
				setBusinessInfoQuery({
					...businessInfoQuery,
					sqftMedium: true
				});
				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Squre Feet: Medium',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				delete queryNew.sqftMedium;
				setBusinessInfoQuery({
					...businessInfoQuery,
					sqftMedium: false
				});

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Squre Feet: Medium',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		} else if (filter.value === 'sqftLarge') {
			if (filter.type === FilterType.INCLUDES) {
				queryNew.sqftLarge = true;
				setBusinessInfoQuery({
					...businessInfoQuery,
					sqftLarge: true
				});

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Square Feet: Large',
							value: 'Included',
							query: queryNew
						}
					});
				}
			} else {
				delete queryNew.sqftLarge;
				setBusinessInfoQuery({
					...businessInfoQuery,
					sqftLarge: false
				});

				if (user && user.email) {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName: 'User changed filters',
						properties: {
							filterChanged: 'Square Feet: Large',
							value: 'Neutral',
							query: queryNew
						}
					});
				}
			}
		}

		queryNew.limit = 50;

		queryNew.skip = 0;

		queryNew.queryType = FilterQueryType.FILTER;

		onFilterChange(queryNew);

		const queryChanged = isQueryDifferent(queryNew);

		if (queryChanged) {
			onGetBusiness(queryNew);
		}
	};

	const filtersCount = useMemo(() => {
		let count = 0;

		if (query?.chains || query?.nonChains) {
			count++;
		}

		if (query?.countOne) {
			count++;
		}

		if (query?.countTwoTen) {
			count++;
		}

		if (query?.countTenPlus) {
			count++;
		}

		if (query?.includePOIs && query?.includePOIs !== undefined) {
			count++;
		}

		if (query?.excludePOIs && query?.excludePOIs !== undefined) {
			count++;
		}

		// Sales Volume filters
		if (query?.salesVolumeLessThan5M) {
			count++;
		}
		if (query?.salesVolume5To10M) {
			count++;
		}
		if (query?.salesVolume10To20M) {
			count++;
		}
		if (query?.salesVolume20To50M) {
			count++;
		}
		if (query?.salesVolume50To100M) {
			count++;
		}
		if (query?.salesVolumeOver100M) {
			count++;
		}

		// Yelp Filters
		if (query?.minYelpReviews || query?.maxYelpReviews) {
			count++;
		}

		// Number of Employees filters
		if (query?.employees1To49) {
			count++;
		}
		if (query?.employees50To99) {
			count++;
		}
		if (query?.employees100To249) {
			count++;
		}
		if (query?.employees250To499) {
			count++;
		}
		if (query?.employeesOver500) {
			count++;
		}
		if (query?.sqftSmall) {
			count++;
		}
		if (query?.sqftMedium) {
			count++;
		}
		if (query?.sqftLarge) {
			count++;
		}

		return count;
	}, [
		query?.chains,
		query?.countOne,
		query?.countTenPlus,
		query?.countTwoTen,
		query?.employees100To249,
		query?.employees1To49,
		query?.employees250To499,
		query?.employees50To99,
		query?.employeesOver500,
		query?.excludePOIs,
		query?.includePOIs,
		query?.maxYelpReviews,
		query?.minYelpReviews,
		query?.nonChains,
		query?.salesVolume10To20M,
		query?.salesVolume20To50M,
		query?.salesVolume50To100M,
		query?.salesVolume5To10M,
		query?.salesVolumeLessThan5M,
		query?.salesVolumeOver100M,
		query?.sqftLarge,
		query?.sqftMedium,
		query?.sqftSmall
	]);

	return (
		<div
			className={`search-tab-filters-list-item ${selectedListItem === 'Business Info' ? 'opened' : ''} ${filtersCount > 0 ? 'filter-selected' : ''} `}
		>
			<SearchTabFiltersListItemHeader
				filterName="Business Info"
				filterCount={filtersCount}
				onClick={clearFilters}
				displayClearFiltersButton={filtersCount > 0}
				icon={
					<InfoWhite className="search-tab-filter-list-item-icon info" />
				}
			/>
			<div className="search-tab-filters-list-item-dropdown">
				<div className="search-tab-filters-list-item-dropdown-divider" />
				<div className="search-tab-filters-list-item-dropdown-filter-group first">
					<h1 className="search-tab-filters-list-item-dropdown-filter-group-header">
						Chain
					</h1>
					<div className="search-tab-filters-list-item-dropdown-filter-group-contacted">
						{!query.chains ? (
							<RadioButtonEmptyWhite
								onClick={() =>
									queryChange({
										type: FilterType.INCLUDES,
										value: 'chains'
									})
								}
							/>
						) : (
							<RadioButtonSelectedGold />
						)}
						<span className="search-tab-filters-list-item-dropdown-filter-group-contacted-inner">
							<Chain className="search-tab-filters-list-item-dropdown-filter-group-contact-info-item-inner-icon chain" />
							Chain
						</span>
					</div>
					<div className="search-tab-filters-list-item-dropdown-filter-group-contacted">
						{!query.nonChains ? (
							<RadioButtonEmptyWhite
								onClick={() =>
									queryChange({
										type: FilterType.INCLUDES,
										value: 'nonChains'
									})
								}
							/>
						) : (
							<RadioButtonSelectedGold />
						)}
						<span className="search-tab-filters-list-item-dropdown-filter-group-contacted-inner">
							<NonChain className="search-tab-filters-list-item-dropdown-filter-group-contact-info-item-inner-icon non-chain" />
							Non-chain
						</span>
					</div>
				</div>
				<div className="search-tab-filters-list-item-dropdown-divider" />
				<div className="search-tab-filters-list-item-dropdown-filter-group">
					<h1 className="search-tab-filters-list-item-dropdown-filter-group-header mt-2">
						# of Locations
						<span>*select all applied</span>
					</h1>
					<div className="search-tab-filters-list-item-dropdown-filter-group-contact-locations">
						<div className="search-tab-filters-list-item-dropdown-filter-group-contact-locations-item">
							{query.countOne ? (
								<CheckboxCheckedGold
									className="search-tab-filters-list-item-dropdown-filter-group-contact-info-checkbox-checked"
									onClick={() =>
										queryChange({
											type: FilterType.EXCLUDES,
											value: 'countOne'
										})
									}
								/>
							) : (
								<CheckboxUncheckedSmall
									className="search-tab-filters-list-item-dropdown-filter-group-contact-info-checkbox-unchecked"
									onClick={() =>
										queryChange({
											type: FilterType.INCLUDES,
											value: 'countOne'
										})
									}
								/>
							)}
							<span className="search-tab-filters-list-item-dropdown-filter-group-contact-info-item-inner">
								1
							</span>
						</div>
						<div className="search-tab-filters-list-item-dropdown-filter-group-contact-locations-item">
							{query.countTwoTen ? (
								<CheckboxCheckedGold
									className="search-tab-filters-list-item-dropdown-filter-group-contact-info-checkbox-checked"
									onClick={() =>
										queryChange({
											type: FilterType.EXCLUDES,
											value: 'countTwoTen'
										})
									}
								/>
							) : (
								<CheckboxUncheckedSmall
									className="search-tab-filters-list-item-dropdown-filter-group-contact-info-checkbox-unchecked"
									onClick={() =>
										queryChange({
											type: FilterType.INCLUDES,
											value: 'countTwoTen'
										})
									}
								/>
							)}
							<span className="search-tab-filters-list-item-dropdown-filter-group-contact-info-item-inner">
								2-10
							</span>
						</div>
						<div className="search-tab-filters-list-item-dropdown-filter-group-contact-locations-item">
							{query.countTenPlus ? (
								<CheckboxCheckedGold
									className="search-tab-filters-list-item-dropdown-filter-group-contact-info-checkbox-checked"
									onClick={() =>
										queryChange({
											type: FilterType.EXCLUDES,
											value: 'countTenPlus'
										})
									}
								/>
							) : (
								<CheckboxUncheckedSmall
									className="search-tab-filters-list-item-dropdown-filter-group-contact-info-checkbox-unchecked"
									onClick={() =>
										queryChange({
											type: FilterType.INCLUDES,
											value: 'countTenPlus'
										})
									}
								/>
							)}
							<span className="search-tab-filters-list-item-dropdown-filter-group-contact-info-item-inner">
								10+
							</span>
						</div>
					</div>
				</div>
				<div className="search-tab-filters-list-item-dropdown-divider" />
				<div className="search-tab-filters-list-item-dropdown-filter-group">
					<h1 className="search-tab-filters-list-item-dropdown-filter-group-header mt-2">
						Online Review Count
					</h1>
					<div className="flex w-full flex-col space-y-2">
						<div className="flex flex-row gap-3">
							<input
								className="h-[1.5rem] w-full rounded p-2 text-left font-barlow text-[0.875rem] font-normal leading-[1.188rem] ring-dm-grey [appearance:textfield] focus:bg-dm-white focus:text-dm-black focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
								placeholder="Min"
								type="number"
								min="0"
								value={
									yelpReviewsFilterMin === null
										? ''
										: yelpReviewsFilterMin
								}
								onChange={e =>
									setYelpReviewsFilterMin(
										e.target.value === ''
											? ''
											: Number(e.target.value)
									)
								}
							/>
							<input
								className="h-[1.5rem] w-full rounded p-2 text-left font-barlow text-[0.875rem] font-normal leading-[1.188rem] ring-dm-grey [appearance:textfield] focus:bg-dm-white focus:text-dm-black focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
								placeholder="Max"
								type="number"
								min="0"
								value={
									yelpReviewsFilterMax === null
										? ''
										: yelpReviewsFilterMax
								}
								onChange={e =>
									setYelpReviewsFilterMax(
										e.target.value === ''
											? ''
											: Number(e.target.value)
									)
								}
							/>
						</div>
						<div className="flex flex-col space-y-2">
							<Button
								className="w-full"
								disabled={yelpReviewsApplyButtonDisabledCheck}
								variant="secondary"
								onClick={yelpReviewsApplyButtonClickHandler}
							>
								Apply
							</Button>
						</div>
					</div>
				</div>
				<div className="search-tab-filters-list-item-dropdown-divider" />
				<div className="search-tab-filters-list-item-dropdown-filter-group mt-5">
					<h1 className="search-tab-filters-list-item-dropdown-filter-group-header">
						Has Staff Info
					</h1>
					<div className="search-tab-filters-list-item-dropdown-filter-group-contacted">
						{!query.includePOIs ? (
							<RadioButtonEmptyWhite
								onClick={() =>
									queryChange({
										type: FilterType.INCLUDES,
										value: 'includePOIs'
									})
								}
							/>
						) : (
							<RadioButtonSelectedGold />
						)}
						<span className="search-tab-filters-list-item-dropdown-filter-group-contacted-inner">
							<StaffGreen className="search-tab-filters-list-item-dropdown-filter-group-contact-info-item-inner-icon chain" />
							Yes
						</span>
					</div>
					<div className="search-tab-filters-list-item-dropdown-filter-group-contacted">
						{!query.excludePOIs ? (
							<RadioButtonEmptyWhite
								onClick={() =>
									queryChange({
										type: FilterType.EXCLUDES,
										value: 'includePOIs'
									})
								}
							/>
						) : (
							<RadioButtonSelectedGold />
						)}
						<span className="search-tab-filters-list-item-dropdown-filter-group-contacted-inner">
							<StaffOrange className="search-tab-filters-list-item-dropdown-filter-group-contact-info-item-inner-icon chain" />
							No
						</span>
					</div>
				</div>
				<div className="search-tab-filters-list-item-dropdown-divider" />
				<div className="search-tab-filters-list-item-dropdown-filter-group">
					<h1 className="search-tab-filters-list-item-dropdown-filter-group-header nowrap mt-2 flex gap-2">
						<SalesVolumeMoney /> Sales Volume
					</h1>
					<div className="search-tab-filters-list-item-dropdown-filter-group-contact-locations sales-volume">
						{[
							{ key: 'salesVolumeLessThan5M', label: '< $5m' },
							{ key: 'salesVolume5To10M', label: '$5-10M' },
							{ key: 'salesVolume10To20M', label: '$10-20M' },
							{ key: 'salesVolume20To50M', label: '$20-50M' },
							{ key: 'salesVolume50To100M', label: '$50-100M' },
							{ key: 'salesVolumeOver100M', label: '>$100M' }
						].map(filter => (
							<div
								key={filter.key}
								className="search-tab-filters-list-item-dropdown-filter-group-contact-locations-item-sales-volume"
							>
								{query[filter.key] ? (
									<CheckboxCheckedGold
										className="search-tab-filters-list-item-dropdown-filter-group-contact-info-checkbox-checked"
										onClick={() =>
											queryChange({
												type: FilterType.EXCLUDES,
												value: filter.key
											})
										}
									/>
								) : (
									<CheckboxUncheckedSmall
										className="search-tab-filters-list-item-dropdown-filter-group-contact-info-checkbox-unchecked"
										onClick={() =>
											queryChange({
												type: FilterType.INCLUDES,
												value: filter.key
											})
										}
									/>
								)}
								<span className="search-tab-filters-list-item-dropdown-filter-group-contact-info-item-inner-sales-volume">
									{filter.label}
								</span>
							</div>
						))}
					</div>
				</div>
				<div className="search-tab-filters-list-item-dropdown-divider" />

				<div className="search-tab-filters-list-item-dropdown-filter-group">
					<h1 className="search-tab-filters-list-item-dropdown-filter-group-header nowrap mt-2 flex gap-2">
						# of Employees
					</h1>
					<div className="search-tab-filters-list-item-dropdown-filter-group-contact-locations employees">
						{[
							{ key: 'employees1To49', label: '1-49' },
							{ key: 'employees50To99', label: '50-99' },
							{ key: 'employees100To249', label: '100-249' },
							{ key: 'employees250To499', label: '250-499' },
							{ key: 'employeesOver500', label: '>500' }
						].map(filter => (
							<div
								key={filter.key}
								className="search-tab-filters-list-item-dropdown-filter-group-contact-locations-item-employees"
							>
								{query[filter.key] ? (
									<CheckboxCheckedGold
										className="search-tab-filters-list-item-dropdown-filter-group-contact-info-checkbox-checked"
										onClick={() =>
											queryChange({
												type: FilterType.EXCLUDES,
												value: filter.key
											})
										}
									/>
								) : (
									<CheckboxUncheckedSmall
										className="search-tab-filters-list-item-dropdown-filter-group-contact-info-checkbox-unchecked"
										onClick={() =>
											queryChange({
												type: FilterType.INCLUDES,
												value: filter.key
											})
										}
									/>
								)}
								<span className="search-tab-filters-list-item-dropdown-filter-group-contact-info-item-inner-employees">
									{filter.label}
								</span>
							</div>
						))}
					</div>
					<div className="search-tab-filters-list-item-dropdown-divider" />

					<div className="search-tab-filters-list-item-dropdown-filter-group">
						<h1 className="search-tab-filters-list-item-dropdown-filter-group-header nowrap flex gap-2">
							<SquareFeetIcon /> Square Feet
						</h1>
						<div className="search-tab-filters-list-item-dropdown-filter-group-contact-locations square-feet">
							{[
								{ key: 'sqftSmall', label: '< 2,500' },
								{ key: 'sqftMedium', label: '2,500-10,000' },
								{ key: 'sqftLarge', label: '> 10,000' }
							].map(filter => (
								<div
									key={filter.key}
									className="search-tab-filters-list-item-dropdown-filter-group-contact-locations-item-square-feet"
								>
									{query[filter.key] ? (
										<CheckboxCheckedGold
											className="search-tab-filters-list-item-dropdown-filter-group-contact-info-checkbox-checked"
											onClick={() =>
												queryChange({
													type: FilterType.EXCLUDES,
													value: filter.key
												})
											}
										/>
									) : (
										<CheckboxUncheckedSmall
											className="search-tab-filters-list-item-dropdown-filter-group-contact-info-checkbox-unchecked"
											onClick={() =>
												queryChange({
													type: FilterType.INCLUDES,
													value: filter.key
												})
											}
										/>
									)}
									<span className="search-tab-filters-list-item-dropdown-filter-group-contact-info-item-inner-square-feet">
										{filter.label}
									</span>
								</div>
							))}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default SearchTabBusinessInfo;
